import {
  RECORD_REFERENCE_TYPE_KEY_MAX_LENGTH,
  RECORD_REFERENCE_TYPE_LABEL_MAX_LENGTH,
  createRecordReferenceTypeSchema,
  type RecordReferenceType,
} from '@easyhelp/shared';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState, type FormEvent } from 'react';
import { SettingsSection } from '../coda/SettingsSection';

type Field = 'key' | 'label';

export interface RecordReferenceTypesSectionProps {
  types: RecordReferenceType[];
  busy?: boolean;
  /** Errore dell'ultima aggiunta (es. 409 per una chiave già usata). */
  addError?: string | null;
  /** Aggiunge un tipo; la promessa risolve quando l'API ha accettato (l'errore è in `addError`). */
  onAdd: (input: { key: string; label: string }) => Promise<unknown>;
  onEdit: (type: RecordReferenceType) => void;
  onRemove: (type: RecordReferenceType) => void;
}

/**
 * Tipi di riferimento al record del prodotto (DEC-014): la chiave stabile che il prodotto manda con
 * la segnalazione e l'etichetta che l'operatore legge nel dettaglio del ticket. La chiave non si
 * modifica dopo la creazione; l'etichetta sì.
 */
export function RecordReferenceTypesSection({
  types,
  busy = false,
  addError = null,
  onAdd,
  onEdit,
  onRemove,
}: RecordReferenceTypesSectionProps) {
  const [draft, setDraft] = useState({ key: '', label: '' });
  const [draftErrors, setDraftErrors] = useState<Partial<Record<Field, string>>>({});

  const change = (field: Field, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setDraftErrors((current) => ({ ...current, [field]: undefined }));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = createRecordReferenceTypeSchema.safeParse(draft);
    if (!parsed.success) {
      const errors: Partial<Record<Field, string>> = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as Field;
        errors[field] ??= issue.message;
      }
      setDraftErrors(errors);
      return;
    }
    setDraftErrors({});
    // L'esito negativo arriva al genitore (`addError`): qui si svuotano i campi solo se è andata bene.
    void onAdd(parsed.data).then(
      () => setDraft({ key: '', label: '' }),
      () => undefined
    );
  };

  return (
    <SettingsSection
      id="record-reference-types-title"
      title="Tipi di riferimento al record"
      description="I tipi di record a cui una segnalazione può riferirsi (per esempio una segnalazione di evento). Il prodotto manda la chiave con la segnalazione; nel dettaglio del ticket l'operatore legge l'etichetta e l'identificativo del record."
    >
      {types.length === 0 ? (
        <Typography color="text.secondary">
          Nessun tipo: il prodotto non può allegare un riferimento al record. Aggiungi i tipi che il prodotto manda; un
          prodotto che usa solo l&apos;identificativo paziente non ne ha bisogno.
        </Typography>
      ) : (
        <List disablePadding aria-label="Tipi di riferimento al record">
          {types.map((type) => (
            <ListItem
              key={type.key}
              divider
              disableGutters
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Modifica l’etichetta">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Modifica l’etichetta di ${type.label}`}
                        disabled={busy}
                        onClick={() => onEdit(type)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Rimuovi">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Rimuovi ${type.label}`}
                        disabled={busy}
                        onClick={() => onRemove(type)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              }
              sx={{ pr: 12 }}
            >
              <ListItemText
                primary={type.label}
                secondary={`Chiave: ${type.key}`}
                slotProps={{ secondary: { sx: { fontFamily: 'monospace' } } }}
              />
            </ListItem>
          ))}
        </List>
      )}
      <form onSubmit={submit} noValidate aria-label="Nuovo tipo di riferimento">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'flex-start' }}>
          <TextField
            label="Chiave"
            value={draft.key}
            onChange={(event) => change('key', event.target.value)}
            error={Boolean(draftErrors.key ?? addError)}
            helperText={draftErrors.key ?? addError ?? 'Es. segnalazione-evento: non si cambia dopo la creazione.'}
            slotProps={{ htmlInput: { maxLength: RECORD_REFERENCE_TYPE_KEY_MAX_LENGTH } }}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Etichetta"
            value={draft.label}
            onChange={(event) => change('label', event.target.value)}
            error={Boolean(draftErrors.label)}
            helperText={draftErrors.label ?? 'Es. Segnalazione di evento: la legge l’operatore.'}
            slotProps={{ htmlInput: { maxLength: RECORD_REFERENCE_TYPE_LABEL_MAX_LENGTH } }}
            sx={{ flex: 1 }}
          />
          <Button type="submit" startIcon={<AddIcon />} variant="outlined" disabled={busy} sx={{ mt: { sm: 2 } }}>
            Aggiungi
          </Button>
        </Stack>
      </form>
      {types.length > 0 && (
        <Alert severity="info">
          Il riferimento arriva solo dai committenti con «Riferimento al record consentito» attivo, nella sezione
          Committenti.
        </Alert>
      )}
    </SettingsSection>
  );
}
